import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../../theme/useTheme';
import FontFamily from '../../../constants/fonts';

export type StatusType = 'active' | 'pending' | 'removal' | 'disabled' | 'inactive';

const STATUS_CONFIG_LIGHT: Record<StatusType, { bg: string; text: string; label: string }> = {
  active:   { bg: '#CAF3C6', text: '#0C420B', label: 'Active' },
  pending:  { bg: '#FEF1B9', text: '#995211', label: 'Pending Activation' },
  removal:  { bg: '#FFE5DE', text: '#AB2B09', label: 'Marked for Removal' },
  disabled: { bg: '#E6E6E6', text: '#4B4B4B', label: 'Disabled' },
  inactive: { bg: '#E6E6E6', text: '#4B4B4B', label: 'Inactive' },
};

const STATUS_CONFIG_DARK: Record<StatusType, { bg: string; text: string; label: string }> = {
  active:   { bg: 'rgba(56,182,56,0.22)',   text: '#8FEBA8', label: 'Active' },
  pending:  { bg: 'rgba(217,150,0,0.22)',   text: '#FFD166', label: 'Pending Activation' },
  removal:  { bg: 'rgba(220,53,53,0.22)',   text: '#FFB09A', label: 'Marked for Removal' },
  disabled: { bg: 'rgba(176,176,176,0.18)', text: '#D4D4D4', label: 'Disabled' },
  inactive: { bg: 'rgba(176,176,176,0.18)', text: '#D4D4D4', label: 'Inactive' },
};

export function getStatusType(status: string): StatusType {
  const lower = status.toLowerCase();
  if (lower === 'active') return 'active';
  if (lower.includes('pending')) return 'pending';
  if (lower.includes('removal') || lower.includes('removed')) return 'removal';
  if (lower === 'disabled') return 'disabled';
  if (lower === 'inactive') return 'inactive';
  return 'disabled';
}

interface AdminStatusBadgeProps {
  status: StatusType | string;
  /** Override label */
  label?: string;
}

const AdminStatusBadge: React.FC<AdminStatusBadgeProps> = ({ status, label }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const type = typeof status === 'string' && !['active','pending','removal','disabled','inactive'].includes(status)
    ? getStatusType(status)
    : status as StatusType;

  const configMap = isDark ? STATUS_CONFIG_DARK : STATUS_CONFIG_LIGHT;
  const config = configMap[type] ?? configMap.disabled;

  return (
    <View style={[styles.badge, { backgroundColor: config.bg }]}>
      <Text style={[styles.label, { color: config.text }]}>{label ?? config.label}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  label: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
  },
});

export default AdminStatusBadge;
